// Single-device FTMS session.
//
// Imperative factory (NOT a React hook) so the ConnectedEquipmentProvider can
// manage N concurrent sessions without violating the rules of hooks. Each
// session owns one Web Bluetooth GATT connection, one parser, one set of
// subscribers, and its own reconnect state. The provider mirrors session
// status into React via `onChange()` callbacks.
//
// Behaviour kept verbatim from the previous useFtmsBike hook:
//   - headline throttle on `snapshot` updates (utils/ble/headline)
//   - full-rate `metricsRef` + `subscribe(cb)` for ref-painting
//   - exponential reconnect via [1000, 3000, 9000] ms
//   - silent reconnect via `navigator.bluetooth.getDevices()` on Chrome 113+
//   - sample accumulation gated by `active` for the session summary

import { FTMS_SERVICE_UUID, EQUIPMENT } from "../utils/ftms";
import { waitForAdvertisement } from "../utils/ble/reconnect";
import { headlineDue, pageHidden } from "../utils/ble/headline";
import { readDeviceInfo } from "../utils/ble/deviceInfo";

const RECONNECT_DELAYS_MS = [1000, 3000, 9000];

/** Status values surfaced to React. */
export const STATUS = Object.freeze({
  IDLE: "idle",
  REQUESTING: "requesting",
  CONNECTING: "connecting",
  CONNECTED: "connected",
  RECONNECTING: "reconnecting",
  ERROR: "error",
});

const emptyMetricsFor = (type) => ({
  type,
  power_watts: null,
  heart_rate_bpm: null,
  elapsed_time_s: null,
  remaining_time_s: null,
  // Optional per-type fields are accessed as `metrics.cadence_rpm ?? null` etc.
});

const summariseSamples = (type, samples) => {
  if (!samples.length) return null;
  const acc = (key) => {
    let count = 0;
    let sum = 0;
    let min = Infinity;
    let max = -Infinity;
    for (const s of samples) {
      const v = s[key];
      if (v == null) continue;
      count += 1;
      sum += v;
      if (v < min) min = v;
      if (v > max) max = v;
    }
    if (count === 0) return null;
    return { avg: sum / count, min, max, count };
  };

  const first = samples[0];
  const last = samples[samples.length - 1];
  const duration_s = Math.round((last.t - first.t) / 1000);
  const out = { type, duration_s, samples: samples.length };

  const power = acc("power_watts");
  if (power) {
    out.avg_power_watts = Math.round(power.avg);
    out.max_power_watts = power.max;
    out.min_power_watts = power.min;
  }

  if (type === "indoor_bike") {
    const cadence = acc("cadence_rpm");
    const speed = acc("speed_kmh");
    if (cadence) {
      out.avg_cadence_rpm = Number(cadence.avg.toFixed(1));
      out.max_cadence_rpm = cadence.max;
    }
    if (speed) {
      out.avg_speed_kmh = Number(speed.avg.toFixed(2));
      out.max_speed_kmh = speed.max;
    }
  } else if (type === "rower") {
    const strokeRate = acc("stroke_rate_spm");
    const pace = acc("pace_sec_per_500m");
    if (strokeRate) {
      out.avg_stroke_rate_spm = Number(strokeRate.avg.toFixed(1));
      out.max_stroke_rate_spm = strokeRate.max;
    }
    if (pace) {
      out.avg_pace_sec_per_500m = Math.round(pace.avg);
      out.best_pace_sec_per_500m = pace.min; // lower is faster
    }
    const lastDistance = last.total_distance_m;
    if (typeof lastDistance === "number") out.total_distance_m = lastDistance;
  }

  return out;
};

const reconnectHintFor = (type) => {
  if (type === "rower") return "wake_device";
  if (type === "indoor_bike") return "wake_trainer";
  return "advertising";
};

const storageKey = (id) => `ftms.device.${id}`;

/**
 * Remember (payload) or forget (null) one device in this browser.
 *
 * Exported so the account-level roster can seed localStorage after a reinstall
 * or a storage wipe — the silent-reconnect path below then finds it as usual.
 */
export const writePersistedDevice = (id, payload) => {
  try {
    if (payload) {
      localStorage.setItem(storageKey(id), JSON.stringify(payload));
    } else {
      localStorage.removeItem(storageKey(id));
    }
  } catch {
    /* storage unavailable */
  }
};

export const listPersistedDevices = () => {
  try {
    const out = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith("ftms.device.")) continue;
      try {
        const v = JSON.parse(localStorage.getItem(key) || "null");
        if (v && v.id && v.type) out.push(v);
      } catch {
        /* skip */
      }
    }
    return out;
  } catch {
    return [];
  }
};

/**
 * Create one FTMS device session bound to a pre-classified BluetoothDevice.
 *
 * @param {Object} opts
 * @param {string} opts.id
 * @param {string} opts.name
 * @param {string} opts.type           - EquipmentType
 * @param {() => boolean} opts.isActive - workout-active gate for sample accumulation
 * @param {() => void} opts.onChange   - fires whenever React-visible state changes
 */
export function createFtmsDeviceSession({ id, name, type, isActive, onChange }) {
  const descriptor = EQUIPMENT[type];
  if (!descriptor) throw new Error(`Unknown equipment type: ${type}`);

  let status = STATUS.IDLE;
  let error = null;
  let hint = null;
  let btDevice = null;
  let characteristic = null;
  let reconnectAttempt = 0;
  let reconnectTimer = null;
  let retrying = false;
  let lastHeadlineAt = 0;
  // Watchdog: when did we last receive a notification packet? Used by
  // getFreshSnapshot() to refuse stale "zombie" readings if the device has
  // gone silent (e.g. PM5 asleep, FTMS link dropped without GATT noticing).
  let lastPacketAt = 0;

  // Which machine this is, saved with the session (api/lib/machines.js): its
  // Bluetooth name, plus the maker and model it reports once read.
  let machine = { name };
  let infoRead = false;

  const metricsRef = { current: emptyMetricsFor(type) };
  const summaryRef = { current: null };
  const samples = [];
  const subscribers = new Set();

  // Snapshot mirror used by React renders; updated at throttled rate.
  let snapshot = metricsRef.current;

  const setStatus = (next, errMsg = null, hintNext = null) => {
    status = next;
    error = errMsg;
    hint = hintNext;
    onChange?.();
  };

  const notify = (m) => {
    for (const cb of subscribers) {
      try {
        cb(m);
      } catch (err) {
        console.warn(`[ftms:${type}] subscriber threw`, err);
      }
    }
  };

  const handlePacket = (event) => {
    const view = event.target?.value;
    if (!view) return;
    let parsed;
    try {
      parsed = descriptor.parse(view);
    } catch (err) {
      // Parsers are defensive — this branch is essentially unreachable, but
      // we keep it so a buggy parser can never crash a live stream.
      console.warn(`[ftms:${type}] parse failed`, err);
      return;
    }
    // FTMS splits metrics across alternating packets (the Rower "More Data"
    // flag, some bikes), so any given `parsed` legitimately carries nulls for
    // fields that weren't in this packet. Merge non-null fields into the live
    // ref so the displayed numbers hold their last value instead of flickering
    // to "—" every other packet. This is purely a DISPLAY aid: `notify()` and
    // the sample buffer below keep the raw `parsed` so the telemetry recorder
    // still sees honest per-packet nulls (and the 3 s watchdog still nulls
    // everything on genuine silence).
    const merged = { ...metricsRef.current };
    for (const k in parsed) {
      if (parsed[k] != null) merged[k] = parsed[k];
    }
    merged.type = type;
    metricsRef.current = merged;
    lastPacketAt = performance.now();

    if (isActive?.()) {
      samples.push({ ...parsed, t: Date.now() });
      summaryRef.current = { ...summariseSamples(type, samples), machine };
    }

    notify(parsed);

    // Keep the snapshot current even when we don't repaint, so the numbers
    // are right the moment the app comes back into view.
    snapshot = merged;
    const now = Date.now();
    if (headlineDue(lastHeadlineAt, now, { workoutActive: !!isActive?.(), hidden: pageHidden() })) {
      lastHeadlineAt = now;
      onChange?.();
    }
  };

  /**
   * Return the latest metrics ONLY if a packet arrived recently. Used by the
   * telemetry recorder to avoid writing "zombie" rows when the device has
   * silently dropped (no GATT disconnect event but no notifications either).
   * Garmin's industry convention is a 3 s threshold, which tolerates 1-2
   * missed 10 Hz packets without false-nulling.
   */
  const getFreshSnapshot = (maxAgeMs = 3000) => {
    if (!lastPacketAt) return null;
    if (performance.now() - lastPacketAt > maxAgeMs) return null;
    return metricsRef.current;
  };

  const teardownNotifications = async () => {
    const char = characteristic;
    characteristic = null;
    if (!char) return;
    try {
      char.removeEventListener("characteristicvaluechanged", handlePacket);
    } catch {
      /* may already be invalid */
    }
    try {
      await char.stopNotifications();
    } catch {
      /* device may have already disconnected */
    }
  };

  // `scanMs`: listen for the device first, for gear handed back by
  // getDevices() that Chrome hasn't seen since it restarted (#169).
  const attach = async (device, { quiet = false, scanMs = 0 } = {}) => {
    if (!quiet) setStatus(STATUS.CONNECTING);
    if (scanMs && !(await waitForAdvertisement(device, scanMs))) {
      throw new Error("Couldn't find it. Wake it, then tap Reconnect.");
    }
    const server = await device.gatt.connect();
    const svc = await server.getPrimaryService(FTMS_SERVICE_UUID);
    const char = await svc.getCharacteristic(descriptor.dataCharUuid);
    await char.startNotifications();
    char.addEventListener("characteristicvaluechanged", handlePacket);

    characteristic = char;
    btDevice = device;
    reconnectAttempt = 0;
    writePersistedDevice(id, { id, name, type });
    setStatus(STATUS.CONNECTED);
    // Once per session, in the background: a slow read mustn't hold up data.
    if (!infoRead) {
      infoRead = true;
      readDeviceInfo(server).then((info) => {
        machine = { name, ...info };
        if (summaryRef.current) summaryRef.current = { ...summaryRef.current, machine };
      });
    }
  };

  const scheduleReconnect = () => {
    if (reconnectTimer) return;
    const attempt = reconnectAttempt;
    if (attempt >= RECONNECT_DELAYS_MS.length) {
      // Out of retries — the device has likely dropped off the air. Surface a
      // hint so the user knows to wake the hardware up rather than wait.
      setStatus(STATUS.RECONNECTING, "Device hasn't reappeared.", reconnectHintFor(type));
      return;
    }
    const delay = RECONNECT_DELAYS_MS[attempt];
    reconnectAttempt = attempt + 1;
    setStatus(STATUS.RECONNECTING, null, reconnectHintFor(type));
    reconnectTimer = setTimeout(async () => {
      reconnectTimer = null;
      if (!btDevice) return;
      try {
        await attach(btDevice);
      } catch {
        scheduleReconnect();
      }
    }, delay);
  };

  const handleDisconnect = () => {
    characteristic = null;
    if (status === STATUS.IDLE) return;
    scheduleReconnect();
  };

  const connect = async (device, { scanMs = 0 } = {}) => {
    if (status === STATUS.CONNECTING || status === STATUS.REQUESTING) return;
    setStatus(STATUS.CONNECTING);
    try {
      device.addEventListener("gattserverdisconnected", handleDisconnect);
      await attach(device, { scanMs });
    } catch (err) {
      console.warn(`[ftms:${type}] connect failed`, err);
      setStatus(STATUS.ERROR, err?.message || "Could not connect.");
    }
  };

  // Background retry while a workout runs. Quiet: it doesn't flash
  // "Connecting…" every pass, and a miss puts the visible state back exactly
  // as it was. Skips while the post-drop backoff is still running.
  const retry = async (device, { scanMs = 0 } = {}) => {
    if (retrying || reconnectTimer) return false;
    if (status === STATUS.CONNECTED || status === STATUS.CONNECTING || status === STATUS.REQUESTING) {
      return false;
    }
    retrying = true;
    const prev = { status, error, hint };
    try {
      device.addEventListener("gattserverdisconnected", handleDisconnect);
      await attach(device, { quiet: true, scanMs });
      return true;
    } catch {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      setStatus(prev.status, prev.error, prev.hint);
      return false;
    } finally {
      retrying = false;
    }
  };

  const disconnect = async () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    await teardownNotifications();
    if (btDevice) {
      try {
        btDevice.removeEventListener("gattserverdisconnected", handleDisconnect);
      } catch {
        /* ignore */
      }
      try {
        btDevice.gatt?.disconnect();
      } catch {
        /* ignore */
      }
    }
    btDevice = null;
    metricsRef.current = emptyMetricsFor(type);
    snapshot = metricsRef.current;
    lastPacketAt = 0;
    setStatus(STATUS.IDLE);
  };

  const forget = async () => {
    await disconnect();
    writePersistedDevice(id, null);
  };

  const subscribe = (cb) => {
    subscribers.add(cb);
    return () => subscribers.delete(cb);
  };

  const resetSession = () => {
    samples.length = 0;
    summaryRef.current = null;
    lastHeadlineAt = 0;
  };

  return {
    id,
    name,
    type,
    get status() {
      return status;
    },
    get error() {
      return error;
    },
    get hint() {
      return hint;
    },
    get snapshot() {
      return snapshot;
    },
    metricsRef,
    summaryRef,
    subscribe,
    connect,
    retry,
    disconnect,
    forget,
    resetSession,
    getFreshSnapshot,
  };
}
